import React, { createElement, forwardRef } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';

export const TEXT_VARIANTS = {
  TITLE_BOLD: 'title-bold',
  TITLE: 'title',
  SUBHEADER: 'subheader',
  SUBHEADER_BOLD: 'subheader-bold',
  BODY: 'body',
  BODY_ON_DARK: 'body-on-dark',
  BODY_BOLD: 'body-bold',
  BODY_LIGHT: 'body-light',
  BODY_LINK: 'body-link',
  INSTRUX_SEMIBOLD: 'instrux-semibold',
  INSTRUX_SEMIBOLD_DARK: 'instrux-semibold-dark',
  INSTRUX_ERROR: 'instrux-error',
  INSTRUX_SEMIBOLD_SUCCESS: 'instrux-semibold-success',
  INSTRUX_SMALL_SEMIBOLD: 'instrux-small-semibold',
  INSTRUX_SMALL_SEMIBOLD_LINK: 'instrux-small-semibold-link',
  INSTRUX_SMALL_SEMIBOLD_ERROR: 'instrux-small-semibold-error',
  TAG_BOLD_ON_DARK: 'tag-bold-on-dark',
  TAG_BOLD: 'tag-bold',
  MANTLE_BODY: 'mantle-body',
  MANTLE_BODY_SEMIBOLD: 'mantle-body-semibold',
};

export const TEXT_COLORS = {
  TAG_BOLD: 'tag-bold',
  ON_PRIMARY: 'on-primary',
  ON_SECONDARY: 'on-secondary',
  SUCCESS_DARK: 'success-dark',
  ERROR_DARK: 'error-dark',
};

const TEXT_VARIANT_ARRAY = Object.values(TEXT_VARIANTS);
const TEXT_COLOR_ARRAY = Object.values(TEXT_COLORS);

const VALID_VARIANTS = TEXT_VARIANT_ARRAY.reduce(
  (accumulator, value) => ({ ...accumulator, [value]: true }),
  {},
);

const VALID_TEXT_COLORS = TEXT_COLOR_ARRAY.reduce(
  (accumulator, value) => ({ ...accumulator, [value]: true }),
  {},
);

const Text = ({
  variant,
  color,
  component = 'span',
  className,
  pendoClassName,
  enableTheme = true,
  children,
  forwardedRef,
  ...props
}) => {
  // Variant was invalid, render nothing
  if (!VALID_VARIANTS[variant]) {
    if (process.env.NODE_ENV === 'development') {
      console.error(
        `Expected \`variant\` prop to be one of ${TEXT_VARIANT_ARRAY.join(
          ', ',
        )}, but instead received \`${variant}\``,
      );
    }
    return null;
  }

  const classes = cn(
    'llui-text',
    {
      'llui-theme-text': enableTheme,
      [`llui-text-${variant}`]: VALID_VARIANTS[variant],
      [`llui-text-color-${color}`]: VALID_TEXT_COLORS[color],
    },
    className,
    pendoClassName,
  );

  return createElement(
    component,
    { className: classes, ref: forwardedRef, ...props },
    children,
  );
};

Text.propTypes = {
  className: PropTypes.string,
  variant: PropTypes.oneOf(TEXT_VARIANT_ARRAY).isRequired,
  color: PropTypes.oneOf(TEXT_COLOR_ARRAY),
  component: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  children: PropTypes.node,
  enableTheme: PropTypes.bool,
  forwardedRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),
};

// Expose dom ref for consumers
export default forwardRef((props, ref) => (
  <Text {...props} forwardedRef={ref} />
));
